// Session-replay chunk transport.
//
// Buffers rrweb events, gzip-compresses a chunk and POSTs it to /v1/replay.
// Separate from transport.js because the body is opaque compressed bytes, not
// the JSON beacon envelope — chunk metadata rides the query string instead.
// See docs/session-replay.md §3.
//
//   - Buffer rrweb events; flush every FLUSH_INTERVAL_MS, or when the buffer
//     hits FLUSH_MAX_EVENTS, or on visibilitychange -> hidden / pagehide /
//     freeze.
//   - Mid-session: gzip off the critical path, POST via plain fetch (no
//     keepalive — its 64 KB body cap drops the FullSnapshot-bearing chunk).
//   - On pagehide/freeze: no time for async gzip — send raw via sendBeacon.

import { supports, onIdle, now } from './safe.js';
import { gzip, strBytes } from './compress.js';

const REPLAY_SCHEMA_VERSION = 1;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_MAX_EVENTS = 100;
const RRWEB_FULL_SNAPSHOT = 2;          // rrweb EventType.FullSnapshot

let _cfg = null;          // { replayUrl, beaconUrl, token, sessionId, recordingId, environment }
let _buf = [];
let _seq = 0;
let _timer = null;
let _started = false;
let _canGzip = false;

export function initReplayTransport(cfg) {
  _cfg = cfg;
  _canGzip = supports('compressionStream');
  _started = true;

  if (supports('visibilityChange')) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushReplay('hidden');
    }, true);
  }
  window.addEventListener('pagehide', () => flushReplay('pagehide'), true);
  window.addEventListener('freeze', () => flushReplay('freeze'), true);

  _timer = setInterval(() => flushReplay('interval'), FLUSH_INTERVAL_MS);
}

// Append one rrweb event. Flushes once the buffer crosses the count threshold.
export function pushReplayEvent(event) {
  if (!_started) return;
  _buf.push(event);
  if (_buf.length >= FLUSH_MAX_EVENTS) onIdle(() => flushReplay('threshold'));
}

// Append a batch and flush it immediately — used for the error-triggered
// ring-buffer dump, where `opts.err` flags the chunk as error-triggered.
export function pushReplayBatch(events, opts) {
  if (!_started || !events || !events.length) return;
  for (let i = 0; i < events.length; i++) _buf.push(events[i]);
  flushReplay('error', opts);
}

export function flushReplay(reason, opts) {
  if (!_started || !_buf.length) return;
  const events = _buf;
  _buf = [];
  // pagehide/freeze are the only paths where the page may die before an async
  // gzip resolves; everywhere else (including 'hidden') fetch keepalive is fine.
  const sync = reason === 'pagehide' || reason === 'freeze';
  buildAndSend(events, !!(opts && opts.err), sync);
}

function buildAndSend(events, err, sync) {
  const seq = _seq++;
  const t0 = events[0].timestamp || now();
  const t1 = events[events.length - 1].timestamp || t0;
  let hasFull = 0;
  for (let i = 0; i < events.length; i++) {
    if (events[i].type === RRWEB_FULL_SNAPSHOT) { hasFull = 1; break; }
  }

  let json;
  try { json = JSON.stringify(events); }
  catch (_) { return; }                  // unserialisable; drop the chunk

  const qs = (enc) => '?sid=' + encodeURIComponent(_cfg.sessionId) +
    '&rid=' + encodeURIComponent(_cfg.recordingId) +
    '&seq=' + seq +
    '&t0=' + t0 + '&t1=' + t1 +
    '&n=' + events.length +
    '&full=' + hasFull + '&err=' + (err ? 1 : 0) +
    '&enc=' + enc +
    '&env=' + encodeURIComponent(_cfg.environment) +
    '&sv=' + REPLAY_SCHEMA_VERSION;

  if (_canGzip && !sync) {
    gzip(json).then(
      (bytes) => post(qs('gzip'), bytes, false),
      () => post(qs('none'), strBytes(json), false)
    );
  } else {
    // No CompressionStream, or an unload path with no time to gzip — send raw.
    post(qs('none'), strBytes(json), sync);
  }
}

function post(qs, bytes, sync) {
  // pagehide/freeze: sendBeacon (queued by the browser, survives the unload).
  // It cannot set headers, so the token rides the URL path (beaconUrl).
  if (sync && supports('sendBeacon')) {
    try {
      if (navigator.sendBeacon(_cfg.beaconUrl + qs, new Blob([bytes], { type: 'text/plain' }))) return;
    } catch (_) { /* fall through */ }
  }
  // Mid-session: plain fetch. We DO NOT set keepalive — Chrome/Firefox cap the
  // total body of keepalive requests at 64 KB per tab, and the first chunk of
  // every recording carries rrweb's FullSnapshot (often >64 KB gzipped), so a
  // keepalive POST silently rejects it. On pagehide/freeze the sendBeacon path
  // above already covers the "survive unload" case.
  try {
    const headers = { 'content-type': 'text/plain' };
    if (_cfg.token) headers['x-amb-token'] = _cfg.token;
    fetch(_cfg.replayUrl + qs, {
      method: 'POST', body: bytes, headers,
      credentials: 'omit', mode: 'cors'
    }).catch(() => { /* silent */ });
    return;
  } catch (_) { /* fall through */ }
  if (supports('sendBeacon')) {
    try { navigator.sendBeacon(_cfg.beaconUrl + qs, new Blob([bytes], { type: 'text/plain' })); }
    catch (_) { /* silent */ }
  }
}
